import { Fragment, useEffect, useState } from "react";
import { Breadcrumbs } from "../../../AbstractElements";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { ProductList } from "../../Components/order/ProductList";
import { UserDeliverySection } from "../../Components/order/UserDeliverySection";
import { toast } from "react-toastify";
import { SelectedProductsCards } from "../../Components/order/SelectedProductsCards";
import DeliveryAddressSelector from "../../Components/order/DeliveryAddressSelector";

export const CreateOrder = () => {
  const navigate = useNavigate();

  const [selectedUser, setSelectedUser] = useState(null);
  const [deliveryType, setDeliveryType] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [shipping, setShipping] = useState(null);
  const [currency, setCurrency] = useState("COP");
  const [createdAt, setCreatedAt] = useState("");
  const [shippingCost, setShippingCost] = useState(0);
  const [carrier, setCarrier] = useState("");

  const { getData, postData } = useDataFetch();

  /**
   * Crear orden
   */
  const { mutateAsync: createOrder, isPending } = postData(
    "/order/byUserDash",
    {},
    {
      onSuccess: (response) => {
        if (response.message === "create_order_success") {
          toast.success("Orden creada exitosamente");
          navigate("/order");
        } else {
          toast.error("No se pudo crear la orden.");
        }
      },
    }
  );

  /**
   * Productos disponibles para el usuario
   */
  const {
    data: responseProductsByUser,
    isLoading: loadingProducts,
  } = getData(
    ["productsByUser", selectedUser?.value],
    selectedUser
      ? `/product/byUserDash/${selectedUser.value}`
      : null,
    {
      enabled: !!selectedUser?.value,
      staleTime: 0,
    }
  );

  /**
   * Direcciones guardadas del usuario
   */
  const {
    data: responseAddressesByUser,
    isLoading: loadingAddresses,
  } = getData(
    ["addressesByUser", selectedUser?.value],
    selectedUser && deliveryType === "DELIVERY"
      ? `/address/byUserDash/${selectedUser.value}`
      : null,
    {
      enabled:
        !!selectedUser?.value &&
        deliveryType === "DELIVERY",
      staleTime: 0,
    }
  );

  const productsByUser =
    responseProductsByUser?.products || [];

  const addresses =
    responseAddressesByUser?.data?.addresses || [];

  /**
   * Verificar si un producto está seleccionado
   */
  const isSelected = (productId) =>
    selectedProducts.some(
      (product) => product.productId === productId
    );

  /**
   * Seleccionar / deseleccionar producto
   */
  const toggleProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts((prev) => [
        ...prev,
        {
          productId,
          quantity: 1,
        },
      ]);
    } else {
      setSelectedProducts((prev) =>
        prev.filter(
          (product) =>
            product.productId !== productId
        )
      );
    }
  };

  /**
   * Actualizar cantidad
   */
  const updateQuantity = (
    productId,
    quantity
  ) => {
    setSelectedProducts((prev) =>
      prev.map((product) =>
        product.productId === productId
          ? {
              ...product,
              quantity: Math.max(
                1,
                parseInt(quantity) || 1
              ),
            }
          : product
      )
    );
  };

  /**
   * Obtener información completa del producto
   */
  const getProductDetails = (id) =>
    productsByUser.find(
      (product) => product.id === id
    ) || {};

  /**
   * Obtener precio del producto
   */
  const getPrice = (product) =>
    parseFloat(
      product?.aditionalData?.[0]
        ?.price_local_currency ?? 0
    );

  /**
   * Calcular subtotal
   */
  const subtotal = selectedProducts.reduce(
    (accumulator, item) => {
      const product = getProductDetails(
        item.productId
      );

      const price = getPrice(product);

      return (
        accumulator +
        price * item.quantity
      );
    },
    0
  );

  /**
   * Si no es DELIVERY, eliminar costo de envío
   */
  useEffect(() => {
    if (deliveryType !== "DELIVERY") {
      setShippingCost(0);
    }
  }, [deliveryType]);

  /**
   * Total de la orden
   */
  const total =
    subtotal + shippingCost;

  /**
   * Crear orden
   */
  const handleSubmit = async () => {
    if (!selectedUser) {
      toast.error(
        "Debes seleccionar un usuario."
      );
      return;
    }

    if (!deliveryType) {
      toast.error(
        "Debes seleccionar el tipo de entrega."
      );
      return;
    }

    /**
     * Validaciones de DELIVERY
     */
    if (deliveryType === "DELIVERY") {
      /**
       * Debe existir una dirección guardada
       * o una dirección manual
       */
      if (
        !selectedAddress &&
        !shipping
      ) {
        toast.error(
          "Debes ingresar una dirección de entrega o seleccionar una existente."
        );
        return;
      }

      /**
       * Validar dirección manual
       */
      if (
        !selectedAddress &&
        shipping
      ) {
        const requiredFields = [
          "name",
          "address",
          "country_id",
          "province_id",
          "city_id",
          "postal_code",
        ];

        const missingFields =
          requiredFields.filter(
            (field) =>
              !shipping[field] ||
              shipping[field]
                .toString()
                .trim() === ""
          );

        if (
          missingFields.length > 0
        ) {
          toast.error(
            "Completa todos los campos requeridos de la dirección manual."
          );
          return;
        }

        const receiver = shipping.receiver;
        if (
          receiver?.type === "other" &&
          !(receiver.name?.trim() && receiver.document?.trim() && receiver.phone?.trim())
        ) {
          toast.error(
            "Completa nombre, documento y teléfono de quien recibe el pedido."
          );
          return;
        }
      }
    }

    /**
     * Validar productos
     */
    if (
      selectedProducts.length === 0
    ) {
      toast.error(
        "Debes seleccionar al menos un producto."
      );
      return;
    }

    /**
     * Validar cantidades
     */
    const hasInvalidQuantity =
      selectedProducts.some(
        (item) =>
          !item.quantity ||
          item.quantity < 1
      );

    if (hasInvalidQuantity) {
      toast.error(
        "Todas las cantidades deben ser mayores o iguales a 1."
      );
      return;
    }

    /**
     * Validar método de pago
     */
    if (!paymentMethod) {
      toast.error(
        "Debes seleccionar un método de pago."
      );
      return;
    }

    
    const body = {
      user_id:
        selectedUser.value,

      address_id:
        deliveryType === "DELIVERY"
          ? selectedAddress
          : null,

      shipping:
        deliveryType === "DELIVERY"
          ? !selectedAddress
            ? shipping
            : null
          : null,

      items:
        selectedProducts.map(
          (item) => ({
            product_id:
              item.productId,
            quantity:
              item.quantity,
          })
        ),

      payment_method:
        paymentMethod,

      delivery_type:
        deliveryType,

      currency,

      created_at:
        createdAt,
      carrier_id: carrier,
      shipping_cost: shippingCost ?? 0,
    };

    try {
      await createOrder(body);
    } catch (error) {
      console.error(
        "Error creando orden:",
        error
      );

      toast.error(
        error?.message ||
          "Error al crear la orden."
      );
    }
  };

  /**
   * Incrementar cantidad
   */
  const handleIncrement = (
    productId
  ) => {
    setSelectedProducts(
      (prev) =>
        prev.map((item) =>
          item.productId ===
          productId
            ? {
                ...item,
                quantity:
                  item.quantity + 1,
              }
            : item
        )
    );
  };

  /**
   * Decrementar cantidad
   */
  const handleDecrement = (
    productId
  ) => {
    setSelectedProducts(
      (prev) =>
        prev.map((item) =>
          item.productId ===
            productId &&
          item.quantity > 1
            ? {
                ...item,
                quantity:
                  item.quantity - 1,
              }
            : item
        )
    );
  };

  /**
   * Eliminar producto
   */
  const handleRemoveProduct = (
    productId
  ) => {
    setSelectedProducts(
      (prev) =>
        prev.filter(
          (item) =>
            item.productId !==
            productId
        )
    );
  };

  /**
   * Formatear precios
   */
  const formatPrice = (
    value,
    currency = "COP"
  ) => {
    return new Intl.NumberFormat(
      "es-CO",
      {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      }
    ).format(value);
  };

  /**
   * Cuando cambia el usuario,
   * limpiar toda la información
   */
  useEffect(() => {
    setDeliveryType("");
    setSelectedAddress("");
    setSelectedProducts([]);
    setPaymentMethod("");
    setCreatedAt("");
    setShipping(null);
    setShippingCost(0);
  }, [selectedUser]);

  return (
    <Fragment>
      <Breadcrumbs
        parent="Tienda"
        title="Crear Orden"
        mainTitle="Crear Orden"
      />

      <Container fluid>
        <Row>
          <Col sm="12">
            <Form>

              {/* Usuario / entrega / pago */}
              <UserDeliverySection
                setSelectedUser={
                  setSelectedUser
                }
                paymentMethod={
                  paymentMethod
                }
                setPaymentMethod={
                  setPaymentMethod
                }
                deliveryType={
                  deliveryType
                }
                setDeliveryType={
                  setDeliveryType
                }
                currency={currency}
                setCurrency={
                  setCurrency
                }
                createdAt={createdAt}
                setCreatedAt={
                  setCreatedAt
                }
              />

              <Row className="gx-3 align-items-start">

                {/* =========================
                    LADO IZQUIERDO
                ========================== */}
                <Col md={5}>
                  <Card
                    style={{
                      height: "500px",
                    }}
                  >
                    <CardBody
                      style={{
                        height: "100%",
                        overflowY:
                          "auto",
                      }}
                    >
                      <ProductList
                        isSelected={
                          isSelected
                        }
                        loadingProducts={
                          loadingProducts
                        }
                        productsByUser={
                          productsByUser
                        }
                        selectedProducts={
                          selectedProducts
                        }
                        toggleProduct={
                          toggleProduct
                        }
                        updateQuantity={
                          updateQuantity
                        }
                        layout="list"
                        formatPrice={
                          formatPrice
                        }
                      />
                    </CardBody>
                  </Card>
                </Col>

                {/* =========================
                    LADO DERECHO
                ========================== */}
                <Col md={7}>
                  <Card
                    className="d-flex flex-column"
                    style={{
                      minHeight: "500px",
                    }}
                  >
                    <CardBody className="d-flex flex-column gap-3 p-3">

                      {/* =========================
                          PRODUCTOS SELECCIONADOS
                      ========================== */}
                      <div
                        style={{
                          maxHeight:
                            "250px",
                          minHeight:
                            "250px",
                          overflowY:
                            "auto",
                          padding:
                            "0 1rem",
                        }}
                      >
                        {selectedProducts.length ===
                        0 ? (
                          <div
                            className="d-flex justify-content-center align-items-center text-center"
                            style={{
                              height:
                                "100%",
                              minHeight:
                                "200px",
                            }}
                          >
                            <h4 className="text-muted">
                              No has
                              seleccionado
                              ningún
                              producto
                            </h4>
                          </div>
                        ) : (
                          <SelectedProductsCards
                            selectedProducts={
                              selectedProducts
                            }
                            getProductDetails={
                              getProductDetails
                            }
                            getPrice={
                              getPrice
                            }
                            onIncrement={
                              handleIncrement
                            }
                            onDecrement={
                              handleDecrement
                            }
                            onRemove={
                              handleRemoveProduct
                            }
                            formatPrice={
                              formatPrice
                            }
                          />
                        )}
                      </div>

                      {/* =========================
                          DIRECCIÓN
                      ========================== */}
                      <div>
                        {deliveryType ===
                          "DELIVERY" && (
                          <DeliveryAddressSelector
                            selectedUser={
                              selectedUser
                            }
                            addresses={
                              addresses
                            }
                            selectedAddress={
                              selectedAddress
                            }
                            setSelectedAddress={
                              setSelectedAddress
                            }
                            loadingAddresses={
                              loadingAddresses
                            }
                            setShipping={
                              setShipping
                            }
                            setShippingCost={
                              setShippingCost
                            }

                            /**
                             * Productos seleccionados
                             * para el cotizador
                             */
                            selectedProducts={
                              selectedProducts
                            }

                            /**
                             * Información completa
                             * de los productos
                             */
                            productsByUser={
                              productsByUser
                            }
                            shippingCost={shippingCost}
                            carrier={carrier}
                            setCarrier={setCarrier}
                          />
                        )}

                        {/* =========================
                            RESUMEN
                        ========================== */}
                        <div className="d-flex justify-content-end align-items-center mt-3 flex-column">

                          {deliveryType ===
                            "DELIVERY" && (
                            <p className="fw-bold mb-1">
                              Envío:{" "}
                              {formatPrice(
                                shippingCost,
                                currency
                              )}
                            </p>
                          )}

                          <p className="fw-bold mb-1">
                            Subtotal:{" "}
                            {formatPrice(
                              subtotal,
                              currency
                            )}
                          </p>

                          <p className="fw-bold mb-2">
                            Total:{" "}
                            {formatPrice(
                              total,
                              currency
                            )}
                          </p>

                          <Button
                            variant="primary"
                            onClick={
                              handleSubmit
                            }
                            disabled={
                              isPending
                            }
                          >
                            <i className="fa fa-check me-2" />

                            {isPending
                              ? "Creando..."
                              : "Crear orden"}
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};